import { useState } from "react";
import { X, Trash2, CreditCard, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { AiExplanation } from "./AiExplanation";
import { TransactionForm } from "./TransactionForm";
import { StatusBadge } from "./Badges";
import { StatusButtons, NotesField } from "./StatusControls";
import { ReceiptsSection } from "./ReceiptsSection";
import { EigenbelegDialog } from "./EigenbelegDialog";
import { api, errMsg } from "../lib/api";
import { fmtDate, fmtNum, fmtEUR } from "../lib/format";

const Facts = ({ tx }) => (
  <div className="grid gap-2 text-sm" data-testid="tx-facts">
    {tx.card && (
      <div className="flex items-center gap-2 text-slate-600" data-testid="tx-card-info">
        <CreditCard className="h-4 w-4 text-slate-400" /> Karte {tx.card.label} · {fmtDate(tx.card.date)} {tx.card.time || ""}
      </div>
    )}
    {tx.fx && (tx.fx.original_currency !== "EUR" || tx.fx.fees) && (
      <div className="flex items-start gap-2 text-slate-600" data-testid="tx-fx-info">
        <Globe2 className="mt-0.5 h-4 w-4 text-slate-400" />
        <span className="font-num">
          {fmtNum(tx.fx.original_amount)} {tx.fx.original_currency}
          {tx.fx.exchange_rate ? ` · Kurs ${String(tx.fx.exchange_rate).replace(".", ",")}` : ""}
          {tx.fx.fees ? ` · Spesen ${fmtEUR(tx.fx.fees)}` : ""}
          {tx.fx.rate_date ? ` · vom ${fmtDate(tx.fx.rate_date)}` : ""}
        </span>
      </div>
    )}
    <div className="text-xs text-slate-400">Position {tx.position} · Seite {tx.page ?? "–"}{tx.manually_added ? " · manuell hinzugefügt" : ""}{tx.manually_edited ? " · bearbeitet" : ""}</div>
  </div>
);

const DeleteTx = ({ onConfirm }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" size="sm" data-testid="delete-transaction-btn" className="gap-1.5 text-red-700 hover:bg-red-50 hover:text-red-800">
        <Trash2 className="h-4 w-4" /> Löschen
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Buchung löschen?</AlertDialogTitle>
        <AlertDialogDescription>Die Buchung wird endgültig entfernt. Die Saldenprüfung wird neu berechnet.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
        <AlertDialogAction data-testid="confirm-delete-transaction-btn" onClick={onConfirm} className="bg-red-700 hover:bg-red-800">Löschen</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const DetailPanel = ({ tx, onClose, onChanged, onDeleted, onStatus, onRefresh, onConfirmed }) => {
  const [busy, setBusy] = useState(false);
  const [eb, setEb] = useState(false);
  if (!tx)
    return (
      <div data-testid="detail-empty" className="flex h-full items-center justify-center p-8 text-center text-sm text-slate-500">
        Wählen Sie links eine Buchung, um Details und die KI-Erklärung zu sehen.
      </div>
    );

  const save = async (body) => {
    try {
      onChanged(await api.patchTransaction(tx.id, body));
      toast.success("Buchung gespeichert");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reanalyze = async () => {
    setBusy(true);
    try { onChanged(await api.reanalyze(tx.id)); toast.success("Neu analysiert"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.deleteTransaction(tx.id); toast.success("Buchung gelöscht"); onDeleted(tx.id); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-4 p-5" data-testid="detail-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-num text-sm text-slate-500" data-testid="detail-date">{fmtDate(tx.value_date)}</div>
          <div className="truncate font-display text-lg font-bold text-slate-900" data-testid="detail-merchant">{tx.ai?.merchant_clean || tx.counterparty_raw}</div>
          <div className={`font-num text-2xl font-semibold ${tx.amount > 0 ? "text-emerald-700" : "text-slate-900"}`} data-testid="detail-amount">
            {tx.amount > 0 ? "+" : ""}{fmtNum(tx.amount)} €
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="detail-close-btn" aria-label="Schließen"><X className="h-4 w-4" /></Button>
          <StatusBadge status={tx.status} testId="detail-status-badge" />
        </div>
      </div>
      <Facts tx={tx} />
      <StatusButtons tx={tx} onStatus={onStatus} onEigenbeleg={() => setEb(true)} />
      <EigenbelegDialog tx={tx} open={eb} onOpenChange={setEb} onSaved={onRefresh} />
      <ReceiptsSection tx={tx} onChanged={onRefresh} onConfirmed={onConfirmed} />
      <NotesField tx={tx} onSaved={onChanged} />
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Rohtext laut Auszug</div>
        <pre data-testid="detail-raw-text" className="font-num whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-3 text-[13px] leading-relaxed text-slate-800">{tx.booking_text}</pre>
      </div>
      <Tabs defaultValue="ai">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="ai" data-testid="tab-ai">Erklärung</TabsTrigger>
          <TabsTrigger value="edit" data-testid="tab-edit">Felder bearbeiten</TabsTrigger>
        </TabsList>
        <TabsContent value="ai" className="mt-4"><AiExplanation tx={tx} onReanalyze={reanalyze} busy={busy} /></TabsContent>
        <TabsContent value="edit" className="mt-4">
          <TransactionForm tx={tx} onSubmit={save} />
          <div className="mt-3 flex justify-end"><DeleteTx onConfirm={remove} /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
